import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { citizenServices as fallbackServices } from '../../../data/services';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { usePortal } from '../PortalContext';
import { cardAccent } from '../sectionPalette';
import { SectionHeading } from './SectionHeading';

/* Keyword → specific RemixIcon, so each citizen service gets a meaningful icon
   even when the API row carries a generic/empty one. Ordered most-specific first. */
const ICON_RULES: [RegExp, string][] = [
  [/whatsapp/i, 'ri-whatsapp-line'],
  [/grievance|complaint/i, 'ri-feedback-line'],
  [/\brti\b|right\s*to\s*info|information/i, 'ri-eye-line'],
  [/help\s*desk|support|assist/i, 'ri-customer-service-2-line'],
  [/fee|calculat|payment|tax/i, 'ri-calculator-line'],
  [/download|certificate/i, 'ri-download-2-line'],
  [/status|track/i, 'ri-search-eye-line'],
  [/login|sign\s*in|account|profile/i, 'ri-login-box-line'],
  [/contact|call|phone/i, 'ri-phone-line'],
];

const GENERIC_ICONS = new Set(['', 'ri-service-line', 'ri-link', 'ri-links-line', 'ri-global-line']);

/* Honour the original icon supplied by the data/API first; otherwise fall back
   to a keyword match on the title; otherwise a sensible default. */
const resolveCitizenIcon = (title = '', provided = ''): string => {
  if (provided && !GENERIC_ICONS.has(provided)) return provided;
  for (const [re, icon] of ICON_RULES) if (re.test(title)) return icon;
  return 'ri-user-heart-line';
};

export const CitizenServicesSection: React.FC = () => {
  const { t } = useTranslation();
  const [trackingId, setTrackingId] = useState('');
  const [trackingResult, setTrackingResult] = useState<'found' | 'not_found' | null>(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const { ref, isRevealed } = useScrollReveal({ threshold: 0.15 });
  const { publicApi } = usePortal();
  const [citizenServices, setCitizenServices] = useState(fallbackServices);

  useEffect(() => {
    publicApi.get('/citizen-services')
      .then(res => {
        const data = res.data?.data || [];
        if (data.length > 0) {
          setCitizenServices(data.map((s: any) => ({
            icon: s.icn_tx || 'ri-service-line',
            title: s.srvc_nm_tx || '',
            description: s.dscn_tx || '',
            url: s.lnk_tx || '',
          })));
        }
      })
      .catch(() => setCitizenServices(fallbackServices));
  }, []);

  const handleTrack = () => {
    if (!trackingId.trim()) return;
    setTrackingLoading(true);
    setTimeout(() => {
      setTrackingResult(trackingId.toUpperCase().startsWith('DTCP') ? 'found' : 'not_found');
      setTrackingLoading(false);
    }, 1200);
  };

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={`scroll-reveal portal-section portal-surface portal-rule ${isRevealed ? 'revealed' : ''}`}
    >
      <div className="reveal-content portal-container">
        <SectionHeading
          eyebrow="For Citizens"
          title={t('citizen.title')}
          subtitle="Check an application's progress, raise a grievance or reach the help desk — no login required."
        />

        {/* Track Application Bar */}
        <div
          style={{
            backgroundImage:
              'linear-gradient(115deg, var(--portal-accent-dark), var(--portal-accent) 55%, var(--portal-accent-light))',
          }}
          className="relative overflow-hidden rounded-3xl px-5 py-6 sm:px-8 sm:py-7 mb-6 shadow-[0_18px_44px_-16px_rgba(0,0,0,0.45)]"
        >
          {/* Decorative translucent shapes for depth (match the service cards) */}
          <span className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 pointer-events-none" />
          <span className="absolute -bottom-14 -left-10 w-36 h-36 rounded-full bg-white/[0.07] pointer-events-none" />
          <span className="absolute top-4 right-1/3 w-2.5 h-2.5 rounded-full bg-white/20 pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm items-center justify-center flex-shrink-0">
                <i className="ri-search-eye-line text-2xl text-white drop-shadow-sm" />
              </div>
              <div>
                <h3 className="text-white text-lg font-semibold drop-shadow-sm">{t('citizen.trackTitle')}</h3>
                <p className="text-white/80 text-sm mt-1">{t('citizen.trackSubtitle')}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <input
                type="text"
                placeholder={t('citizen.trackPlaceholder')}
                value={trackingId}
                onChange={(e) => { setTrackingId(e.target.value); setTrackingResult(null); }}
                className="w-full sm:w-72 bg-white px-5 py-3 rounded-full text-sm text-gray-800 placeholder:text-gray-400 border border-transparent shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] focus:ring-2 focus:ring-white/60 focus:outline-none transition-all duration-200"
              />
              <button
                onClick={handleTrack}
                disabled={trackingLoading}
                className="bg-white text-[color:var(--portal-accent-dark)] text-sm font-bold px-6 py-3 rounded-full hover:-translate-y-0.5 hover:shadow-[0_10px_24px_-8px_rgba(0,0,0,0.5)] transition-all duration-200 flex items-center justify-center gap-2 flex-shrink-0 disabled:opacity-70 disabled:translate-y-0"
              >
                {trackingLoading && <i className="ri-loader-4-line animate-spin" />}
                {t('citizen.checkStatus')}
              </button>
            </div>
          </div>

          {trackingResult === 'found' && (
            <div className="relative z-10 mt-4 bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl p-4 flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-300 text-lg flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-white text-sm">{t('citizen.found')}</span>
                <span className="text-white/80 text-sm"> — Status: Under Review | Officer: Town Planning Officer, Vijayawada</span>
              </div>
            </div>
          )}
          {trackingResult === 'not_found' && (
            <div className="relative z-10 mt-4 bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl p-4 flex items-start gap-3">
              <i className="ri-error-warning-fill text-red-300 text-lg flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-white text-sm">{t('citizen.notFound')}</span>
                <span className="text-white/80 text-sm"> — Please verify your reference number and try again.</span>
              </div>
            </div>
          )}
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 sm:gap-5 items-stretch">
          {citizenServices.map((svc: any, i: number) => {
            const href = svc.url || '#';
            const isExternal = /^https?:\/\//i.test(href);
            const target = isExternal ? '_blank' : undefined;
            const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
            const [from, to] = cardAccent(i);
            const icon = resolveCitizenIcon(svc.title, svc.icon);
            const cta = isExternal ? 'Open Link' : 'Open';
            return (
              <a
                key={i}
                href={href}
                target={target}
                rel={rel}
                onClick={(e) => { if (!svc.url) e.preventDefault(); }}
                style={{ backgroundImage: `linear-gradient(135deg, ${from}, ${to})` }}
                className="stagger-child group relative flex flex-col h-full rounded-2xl p-4 sm:p-5 overflow-hidden no-underline text-white shadow-[0_4px_14px_rgba(0,0,0,0.10)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_38px_rgba(0,0,0,0.22)]"
              >
                {/* Decorative translucent shapes for depth */}
                <span className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-white/10 pointer-events-none" />
                <span className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-white/[0.07] pointer-events-none" />
                <span className="absolute top-3 left-3 w-2.5 h-2.5 rounded-full bg-white/20 pointer-events-none" />
                {/* Diagonal sheen on hover */}
                <span className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                {/* Icon badge */}
                <div className="relative z-10 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 group-hover:bg-white/30 group-hover:shadow-[0_8px_20px_rgba(0,0,0,0.18)]">
                  <i className={`${icon} text-2xl sm:text-[26px] text-white drop-shadow-sm`} />
                </div>

                {/* Title */}
                <h3 className="relative z-10 mt-3.5 text-[13.5px] sm:text-[15px] font-bold leading-snug tracking-tight line-clamp-2 drop-shadow-sm">
                  {svc.title}
                </h3>

                {/* Description / tagline — high-contrast and large enough to read from a distance */}
                {svc.description && (
                  <p className="relative z-10 mt-2 text-[12.5px] sm:text-[14px] font-semibold text-white leading-relaxed line-clamp-3 flex-1 drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]">
                    {svc.description}
                  </p>
                )}

                {/* CTA — frosted pill button, clearly visible and flips to solid white on hover */}
                <div className="relative z-10 w-full mt-3.5 pt-3 border-t border-white/25">
                  <span className="flex items-center justify-center gap-1.5 w-full rounded-lg bg-white/20 backdrop-blur-sm border border-white/45 px-3 py-2 text-[12px] sm:text-[13px] font-extrabold uppercase tracking-wide text-white shadow-sm transition-all duration-300 group-hover:bg-white group-hover:text-[#1a1a1a] group-hover:shadow-md">
                    {cta}
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
